import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useSharedShowPageContext from '../common/hooks/useSharedShowPageContext';
import useEpisodes from '../common/hooks/useEpisodes';
import ChannelMobileHeader from './ChannelMobileHeader';
import HeroSlateSection from './HeroSlateSection';
import AboutSection from './AboutSection';
import EpisodesHeader from './EpisodesHeader';
import EpisodesSection from './EpisodesSection';
import RelatedSection from './RelatedSection';
import styles from './styles.module.scss';
import { size } from '@/helpers/lodash';
import useShow from '@/hooks/shows/useShowFromURL';
import LayoutWrapperVertical from '@/components/component-library/LayoutWrapperVertical';
import useScrollReachedElement from '@/hooks/browser/useScrollReachedElement';
import LayoutWrapperHorizontal from '@/components/component-library/LayoutWrapperHorizontal';
import Gradient, { GRADIENT_POSITION_BOTTOM, GRADIENT_POSITION_TOP } from '@/components/app/Gradient';
import { classes } from '@/helpers/styling';
import Text, { FONT_SIZE_090 } from '@/components/component-library/Text';
import ButtonTextWithCaret from '@/components/component-library/Button/TextWithCaret';
import Translation from '@/components/component-library/Translation';
import { T_NAMESPACE_SHOWS } from '@/config/translations/namespaces.mjs';

const USE_EPISODES_SCROLL = false;
const MAX_EPISODES_COUNT = 6;

const ShowPageMobile = () => {
  const { relatedRowImpressionRef } = useSharedShowPageContext();
  const [episodesExpanded, setEpisodesExpanded] = useState(false);
  const { show, isLoading } = useShow();
  const { episodes: episodePreviews, isLoading: isLoadingEpisodes } = useEpisodes();

  const { title: showTitle } = show;

  const { asPath } = useRouter();

  // Handle checking of whether scroll has reached the nav element.
  // When the nav element is reached, it should become sticky.
  // The ref should be passed to the nav element. Top offset = header bar (80) - extra padding (25)
  const {
    ref: navRef,
    hasIntersected: mobileHeaderIsActive,
    resetScroll
  } = useScrollReachedElement({
    topOffset: 140,
    debounceTime: 10,
    measureFromTopOfPage: true
  });
  
  useEffect(() => { if (asPath) { resetScroll(); } }, [asPath, resetScroll]);

  const { episodeScrollContainerRef } = useSharedShowPageContext();

  const episodesCannotFit = (size(episodePreviews) > MAX_EPISODES_COUNT);
  const collapseEpisodeList = (USE_EPISODES_SCROLL || !episodesExpanded) && episodesCannotFit;

  const handleExpandEpisodes = useCallback(() => setEpisodesExpanded(true), []);

  return (
    <>
      <ChannelMobileHeader
        active={ mobileHeaderIsActive }
        title={ showTitle }
      />
      <LayoutWrapperVertical
        className={ styles.showPageContentMobile }
        disableTopOnMobile
      >
        <div
          className={ styles.showScrollContent }
          ref={ episodeScrollContainerRef }
        >
          <HeroSlateSection />
          <div ref={ navRef } />
          <div className={ styles.episodes }>
            { USE_EPISODES_SCROLL && (
              <Gradient
                breadth='5%'
                className={ styles.topBannerGradient }
                extraBreadth={ false }
                position={ GRADIENT_POSITION_TOP }
              />
            ) }
            <LayoutWrapperHorizontal
              className={ classes(styles.episodesHeader, { [styles.isScrollable]: USE_EPISODES_SCROLL }) }
              isRow
            >
              <EpisodesHeader />
            </LayoutWrapperHorizontal>
            <div className={ classes(styles.episodesScrollContainer, { [styles.isScrollable]: USE_EPISODES_SCROLL }) }>
              <EpisodesSection maxEpisodes={ collapseEpisodeList ? MAX_EPISODES_COUNT : undefined } />
            </div>
            { collapseEpisodeList && (
              <Gradient
                breadth='50%'
                className={ styles.topBannerGradient }
                directionReverse
                extraBreadth={ false }
                position={ GRADIENT_POSITION_BOTTOM }
              />
            ) }
          </div>
          { !episodesExpanded && episodesCannotFit && (
            <ButtonTextWithCaret className={ classes(styles.viewAllEpisodes, { [styles.isLoading]: isLoading || isLoadingEpisodes }) } loading={ isLoading || isLoadingEpisodes } onClick={ handleExpandEpisodes }>
              <Translation i18nKey='view-all-episodes' namespace={ T_NAMESPACE_SHOWS } />
            </ButtonTextWithCaret>
          ) }
          <LayoutWrapperHorizontal>
            <div className={ classes(styles.aboutTitle, { [styles.episodesAreExpanded]: episodesExpanded }) }>
              <Text
                bold
                loading={ isLoading }
                size={ FONT_SIZE_090 } useHeaderFont
              >
                <Translation i18nKey='about-title' namespace={ T_NAMESPACE_SHOWS } />
              </Text>
            </div>
            <AboutSection />
          </LayoutWrapperHorizontal>
          <div ref={ relatedRowImpressionRef } />
          <RelatedSection />
        </div>
      </LayoutWrapperVertical>
    </>
  );
};

export default ShowPageMobile;
